'use client';

import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { sendContributorSubmissionEmail } from '@/lib/email/emailService';

type UploadValues = {
  contributorName: string;
  contactEmail: string;
  itemTitle: string;
  category: string;
  estimatedDate: string;
  description: string;
  agreeToTerms: boolean;
};

const categories = [
  'Lodge Records',
  'Charters & Bylaws',
  'Physical Artifacts',
  'Regalia & Textiles',
  'Member History',
  'Building & Property',
  'Other / Unsure',
];

// Mock token data — in production this is fetched from the API by parsing the URL token
const mockTokenData = {
  lodgeName: "St. Andrew\'s Lodge No. 1",
  jurisdiction: 'Nova Scotia',
  district: 'District IV',
  tokenId: 'LODGE-1-A8F4K2',
  expiresAt: '06 May 2026',
  usesRemaining: 2,
  isValid: true,
};

export default function ContributorUploadForm() {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [queueRef, setQueueRef] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<UploadValues>({
    defaultValues: {
      contributorName: '',
      contactEmail: '',
      itemTitle: '',
      category: '',
      estimatedDate: '',
      description: '',
      agreeToTerms: false,
    },
  });

  const handleFiles = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files).filter((f) =>
      ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(f.type)
    );
    setUploadedFiles((prev) => [...prev, ...arr]);
  };

  const removeFile = (idx: number) => {
    setUploadedFiles((f) => f.filter((_, i) => i !== idx));
  };

  const onSubmit = async (data: UploadValues) => {
    if (uploadedFiles.length === 0) {
      toast.error('Please attach at least one photograph or scan before submitting');
      return;
    }
    if (!data.agreeToTerms) {
      toast.error('Please confirm your agreement before submitting');
      return;
    }
    // BACKEND INTEGRATION: POST /api/contributor/upload with token validation + FormData
    // Token is read from URL params: /contributor-upload-page?token=LODGE-1-A8F4K2
    await new Promise((r) => setTimeout(r, 1800));
    const ref = `Q-${Date.now().toString(36).toUpperCase().slice(-6)}`;
    setQueueRef(ref);
    setSubmitted(true);
    toast.success('Item submitted to the lodge archive queue');

    // Send confirmation email to contributor if email provided
    if (data.contactEmail) {
      sendContributorSubmissionEmail({
        to: data.contactEmail,
        contributorName: data.contributorName,
        itemTitle: data.itemTitle,
        queueRef: ref,
        lodgeName: mockTokenData.lodgeName,
        fileCount: uploadedFiles.length,
      }).catch(() => {
        // Silent fail — email is best-effort
      });
    }
  };

  if (!mockTokenData.isValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--background)' }}>
        <div className="masonic-card p-8 max-w-md w-full text-center">
          <Icon
            name="ExclamationTriangleIcon"
            size={48}
            style={{ color: 'var(--danger)', margin: '0 auto 16px' } as React.CSSProperties}
          />
          <h2 className="font-serif text-xl font-semibold mb-2" style={{ color: 'var(--foreground)' }}>
            Invalid or Expired Token
          </h2>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            This upload link is no longer valid. Please contact your Lodge Secretary to request a new contributor link.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--background)' }}>
        <div className="masonic-card p-8 max-w-lg w-full text-center animate-fade-in">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ background: 'var(--success-bg)' }}
          >
            <Icon name="CheckCircleIcon" size={36} style={{ color: 'var(--success)' } as React.CSSProperties} />
          </div>
          <h2 className="font-serif text-2xl font-semibold mb-2" style={{ color: 'var(--foreground)' }}>
            Item Submitted Successfully
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
            Your contribution has been added to the incoming queue for{' '}
            <strong>{mockTokenData.lodgeName}</strong>. The Lodge Secretary will review and archive it.
          </p>

          <div
            className="rounded-lg p-4 mb-6 text-left"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
          >
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--muted-foreground)' }}>
              Submission Reference
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Queue Reference</p>
                <p className="font-mono font-bold" style={{ color: 'var(--foreground)' }}>{queueRef}</p>
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Files Submitted</p>
                <p className="font-semibold" style={{ color: 'var(--foreground)' }}>{uploadedFiles.length}</p>
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Lodge</p>
                <p className="font-semibold" style={{ color: 'var(--foreground)' }}>{mockTokenData.lodgeName}</p>
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Submitted</p>
                <p className="font-semibold" style={{ color: 'var(--foreground)' }}>04 May 2026</p>
              </div>
            </div>
          </div>

          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Keep your reference number <strong>{queueRef}</strong> for your records. You will receive a confirmation at the email address provided.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Header */}
      <header
        className="px-6 py-5"
        style={{
          background: 'linear-gradient(180deg, #1B2A4A 0%, #0F1E38 100%)',
          borderBottom: '2px solid rgba(201, 168, 76, 0.3)',
        }}
      >
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AppLogo src="/assets/images/logo-1777919850828.png" size={40} />
            <div>
              <span className="font-serif text-base font-semibold block" style={{ color: '#C9A84C' }}>
                The Masonic Vault
              </span>
              <span className="text-xs" style={{ color: 'rgba(245, 240, 232, 0.6)' }}>
                Contributor Upload Portal
              </span>
            </div>
          </div>
          <div
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{ background: 'rgba(201, 168, 76, 0.1)', border: '1px solid rgba(201, 168, 76, 0.25)' }}
          >
            <Icon name="BuildingLibraryIcon" size={15} style={{ color: '#C9A84C' } as React.CSSProperties} />
            <div>
              <p className="text-xs font-semibold" style={{ color: '#F5F0E8' }}>
                {mockTokenData.lodgeName}
              </p>
              <p className="text-xs" style={{ color: 'rgba(245, 240, 232, 0.6)' }}>
                {mockTokenData.jurisdiction} · {mockTokenData.district}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Token validity strip */}
      <div
        className="px-6 py-2.5 text-center text-xs font-medium"
        style={{ background: 'var(--success-bg)', color: 'var(--success)', borderBottom: '1px solid rgba(45, 106, 79, 0.2)' }}
      >
        <Icon name="ShieldCheckIcon" size={13} className="inline mr-1.5" />
        Secure contributor link · Token {mockTokenData.tokenId} · Expires {mockTokenData.expiresAt} · {mockTokenData.usesRemaining} submissions remaining
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="font-serif text-2xl font-semibold mb-2" style={{ color: 'var(--foreground)' }}>
            Submit a Historical Item
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            You have been invited to contribute to the digital archive of <strong>{mockTokenData.lodgeName}</strong>.
            Upload photographs or scans of historical documents, artifacts, or memorabilia. No account is required.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Contributor info */}
          <div className="masonic-card p-5 space-y-4">
            <div
              className="flex items-center gap-2 pb-4"
              style={{ borderBottom: '1px solid var(--border)' }}
            >
              <Icon name="UserIcon" size={16} style={{ color: 'var(--accent)' } as React.CSSProperties} />
              <h2 className="font-serif text-base font-semibold" style={{ color: 'var(--foreground)' }}>
                Your Information
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="masonic-label">Your Name *</label>
                <input
                  className="masonic-input"
                  placeholder="W. Bro. John MacLeod"
                  {...register('contributorName', { required: 'Please enter your name' })}
                />
                {errors.contributorName && (
                  <p className="text-xs mt-1.5 font-medium" style={{ color: 'var(--danger)' }}>
                    {errors.contributorName.message}
                  </p>
                )}
              </div>
              <div>
                <label className="masonic-label">Contact Email</label>
                <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  For submission confirmation only
                </p>
                <input
                  className="masonic-input"
                  type="email"
                  placeholder="your@email.com"
                  {...register('contactEmail', {
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                  })}
                />
                {errors.contactEmail && (
                  <p className="text-xs mt-1.5 font-medium" style={{ color: 'var(--danger)' }}>
                    {errors.contactEmail.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Item details */}
          <div className="masonic-card p-5 space-y-4">
            <div
              className="flex items-center gap-2 pb-4"
              style={{ borderBottom: '1px solid var(--border)' }}
            >
              <Icon name="ArchiveBoxIcon" size={16} style={{ color: 'var(--accent)' } as React.CSSProperties} />
              <h2 className="font-serif text-base font-semibold" style={{ color: 'var(--foreground)' }}>
                Item Details
              </h2>
            </div>

            <div>
              <label className="masonic-label">Item Title *</label>
              <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                Describe what this item is in a few words
              </p>
              <input
                className="masonic-input"
                placeholder="e.g. Lodge Meeting Minutes, April 1887"
                {...register('itemTitle', { required: 'Please give this item a title' })}
              />
              {errors.itemTitle && (
                <p className="text-xs mt-1.5 font-medium" style={{ color: 'var(--danger)' }}>
                  {errors.itemTitle.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="masonic-label">Category *</label>
                <select
                  className="masonic-select"
                  {...register('category', { required: 'Please select a category' })}
                >
                  <option value="">Select category…</option>
                  {categories.map((c) => (
                    <option key={`contrib-cat-${c}`} value={c}>{c}</option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-xs mt-1.5 font-medium" style={{ color: 'var(--danger)' }}>
                    {errors.category.message}
                  </p>
                )}
              </div>
              <div>
                <label className="masonic-label">Approximate Date</label>
                <input
                  className="masonic-input"
                  placeholder="e.g. April 1887 or circa 1830"
                  {...register('estimatedDate')}
                />
              </div>
            </div>

            <div>
              <label className="masonic-label">Description</label>
              <p className="text-xs mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                Any additional context — where you found it, who it belonged to, what it depicts
              </p>
              <textarea
                className="masonic-input resize-none"
                rows={4}
                placeholder="e.g. Found in my grandfather's belongings. He was a member of St. Andrew's Lodge in the 1920s. The document appears to be handwritten meeting minutes…"
                {...register('description')}
              />
            </div>
          </div>

          {/* File upload */}
          <div className="masonic-card p-5">
            <div
              className="flex items-center gap-2 pb-4 mb-4"
              style={{ borderBottom: '1px solid var(--border)' }}
            >
              <Icon name="PhotoIcon" size={16} style={{ color: 'var(--accent)' } as React.CSSProperties} />
              <h2 className="font-serif text-base font-semibold" style={{ color: 'var(--foreground)' }}>
                Photographs or Scans
              </h2>
              <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>
                JPG, PNG, PDF · Max 50MB each
              </span>
            </div>

            <div
              className="rounded-lg border-2 border-dashed flex flex-col items-center justify-center py-10 px-6 text-center cursor-pointer transition-all duration-150"
              style={{
                borderColor: isDragging ? 'var(--accent)' : 'var(--border)',
                background: isDragging ? 'rgba(201, 168, 76, 0.05)' : 'var(--muted)',
              }}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleFiles(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                style={{ background: 'rgba(27, 42, 74, 0.08)' }}
              >
                <Icon name="ArrowUpTrayIcon" size={24} style={{ color: 'var(--primary)' } as React.CSSProperties} />
              </div>
              <p className="font-semibold text-sm mb-1" style={{ color: 'var(--foreground)' }}>
                {isDragging ? 'Drop files here' : 'Drag files here or tap to browse'}
              </p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                You can upload multiple files — photographs from different angles are encouraged
              </p>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>

            {uploadedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
                  {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} ready to submit
                </p>
                {uploadedFiles.map((f, i) => (
                  <div
                    key={`contrib-file-${i}-${f.name}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                  >
                    <Icon
                      name={f.type === 'application/pdf' ? 'DocumentTextIcon' : 'PhotoIcon'}
                      size={16}
                      style={{ color: 'var(--primary)', flexShrink: 0 } as React.CSSProperties}
                    />
                    <span className="text-sm flex-1 truncate" style={{ color: 'var(--foreground)' }}>
                      {f.name}
                    </span>
                    <span className="text-xs tabular-nums" style={{ color: 'var(--muted-foreground)' }}>
                      {(f.size / 1024 / 1024).toFixed(1)} MB
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="p-0.5 rounded hover:bg-muted transition-colors"
                    >
                      <Icon name="XMarkIcon" size={14} style={{ color: 'var(--muted-foreground)' } as React.CSSProperties} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Agreement & submit */}
          <div className="masonic-card p-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 w-5 h-5 rounded flex-shrink-0"
                style={{ accentColor: 'var(--accent)' }}
                {...register('agreeToTerms', { required: true })}
              />
              <span className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                I confirm that I have the right to submit these materials to the lodge archive, and I understand they will be preserved permanently as part of the historical record of{' '}
                <strong style={{ color: 'var(--foreground)' }}>{mockTokenData.lodgeName}</strong>.
                I agree to the{' '}
                <span className="underline cursor-pointer" style={{ color: 'var(--accent)' }}>
                  Contributor Terms
                </span>.
              </span>
            </label>
            {errors.agreeToTerms && (
              <p className="text-xs mt-2 font-medium" style={{ color: 'var(--danger)' }}>
                You must agree before submitting
              </p>
            )}

            <button
              type="submit"
              className="masonic-btn-gold w-full mt-5"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                  Submitting to archive queue…
                </>
              ) : (
                <>
                  <Icon name="CloudArrowUpIcon" size={18} />
                  Submit Item to {mockTokenData.lodgeName}
                </>
              )}
            </button>
          </div>

          {/* Privacy note */}
          <div
            className="rounded-lg p-4 flex gap-3 text-xs"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
          >
            <Icon name="LockClosedIcon" size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0, marginTop: 1 } as React.CSSProperties} />
            <p style={{ color: 'var(--muted-foreground)' }}>
              Your submission is private and will only be visible to authorised members of{' '}
              {mockTokenData.lodgeName}. The Masonic Vault does not share lodge records publicly.
              Email notifications are sent from support@themasonicvault.com only.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}